// src/types/Performance.ts

import type { Strand } from './Strand';
import type { BandRecommendation, Event, Staff } from './mongodb';

export interface PerformanceAccommodation {
  singleRooms: {
    quantity: number;
    costPerRoom: number;
    totalNights: number;
  };
  twinRooms: {
    quantity: number;
    costPerRoom: number;
    totalNights: number;
  };
  total?: number;
}

export interface PerformanceEquipment {
  rental: number;
  shipping: number;
  backline: number;
  total?: number;
}

export interface PerformanceTechnical {
  soundEngineer: number;
  lightingEngineer: number;
  backline: number;
  other: number;
  total?: number;
}

export interface PerformanceTravel {
  flights: number;
  groundTransport: number;
  other: number;
  total?: number;
}

export interface PerformancePerDiem {
  dailyRate: number;
  numberOfDays: number;
  total?: number;
}

export interface PerformanceAdditional {
  marketing: number;
  insurance: number;
  visas: number;
  total?: number;
}

export interface PerformanceCostsBreakdown {
  fee: number;
  travel: number;
  accommodation: number;
  perDiem: number;
  equipment: number;
  other: number;
  contingency: number;
}

export interface PerformanceCosts {
  bandFee: number;
  travel: PerformanceTravel;
  accommodation: PerformanceAccommodation;
  perDiem: PerformancePerDiem;
  equipment: PerformanceEquipment;
  technical: PerformanceTechnical;
  additional?: PerformanceAdditional;
  marketing?: number;
  insurance?: number;
  visas?: number;
  other?: number;
  otherCosts?: Array<{ description: string; amount: number }>;
  summary?: {
    totalCosts: number;
    contingency: number;
    grandTotal: number;
  };
  currency?: string;
}

export interface PerformanceRevenue {
  ticketPrice: number;
  expectedOccupancy: number;
  sponsorship: number;
  merchandiseSales: number;
  otherRevenue: number;
  expectedCapacity?: number;
}

export interface Performance {
  _id: string;
  strandId?: string | Strand;
  simpleBandName?: string;
  band: string | BandRecommendation;
  event: string | Event;
  venue?: string | any;
  startTime: string | Date;
  endTime: string | Date;
  primaryContact?: string;
  contactEmail?: string;
  contactPhone?: string;
  genre?: string;
  ensembleMakeup?: 'all-male' | 'all-female' | 'mixed' | 'predominantly-male' | 'predominantly-female' | 'female-led' | 'other';
  bookingStatus?: 'PROVISIONAL' | 'CHECKING' | 'BOOKED' | 'AVAILABLE';
  bookingNotes?: string;
  isTicketed: boolean;
  isArchived: boolean;
  archiveDate?: string | Date;
  archiveReason?: 'pastEvent' | 'cancelled' | 'redundant' | 'other';
  archiveNotes?: string;
  status: 'confirmed' | 'pending' | 'cancelled';
  revenue: PerformanceRevenue;
  costs: PerformanceCosts;
  staff: Array<{
    staffId: string | Staff;
    assignedRole?: string;
  }>;
  contingencyRate: number;
  notes?: string;
  programmingId?: string; // Link to programming record
  createdAt: Date;
  updatedAt: Date;
}

// Helper functions
export const calculateTotalCost = (performance: Performance): number => {
  if (!performance || !performance.costs) return 0;
  
  const { costs } = performance;
  
  // Log the entire costs object to debug
  
  // Extract band fee
  const bandFee = Number(costs.bandFee || 0);
  
  // Calculate other costs
  const travelTotal = (costs.travel?.total || 0) || 
    ((costs.travel?.flights || 0) + 
    (costs.travel?.groundTransport || 0) + 
    (costs.travel?.other || 0));
  
  // Fix accommodation calculation to handle potential nested structure
let accommodationTotal = 0;
if (costs.accommodation) {
  if (costs.accommodation.total) {
    accommodationTotal = costs.accommodation.total;
  } else {
    const singleRooms = costs.accommodation.singleRooms || {};
    const twinRooms = costs.accommodation.twinRooms || {};
    
    // Type-guarded check for quantity
    const singleQuantity = typeof singleRooms.quantity === 'number' ? 
      singleRooms.quantity : 
      (typeof singleRooms.quantity === 'object' && singleRooms.quantity !== null ? 
        (Number((singleRooms.quantity as any).quantity) || 0) : 0);
        
    const twinQuantity = typeof twinRooms.quantity === 'number' ? 
      twinRooms.quantity : 
      (typeof twinRooms.quantity === 'object' && twinRooms.quantity !== null ? 
        (Number((twinRooms.quantity as any).quantity) || 0) : 0);
    
    const singleRoomsCost = 
      singleQuantity * 
      (Number(singleRooms.costPerRoom) || 0) * 
      (Number(singleRooms.totalNights) || 1);
      
    const twinRoomsCost = 
      twinQuantity * 
      (Number(twinRooms.costPerRoom) || 0) * 
      (Number(twinRooms.totalNights) || 1);
      
    accommodationTotal = singleRoomsCost + twinRoomsCost;
  }
}
  
  const perDiemTotal = (costs.perDiem?.total || 0) || 
    ((costs.perDiem?.dailyRate || 0) * 
     (costs.perDiem?.numberOfDays || 1));
  
  const equipmentTotal = (costs.equipment?.total || 0) || 
    ((costs.equipment?.rental || 0) + 
     (costs.equipment?.shipping || 0) +
     (costs.equipment?.backline || 0));
  
  const technicalTotal = (costs.technical?.total || 0) || 
    ((costs.technical?.soundEngineer || 0) + 
     (costs.technical?.lightingEngineer || 0) + 
     (costs.technical?.backline || 0) + 
     (costs.technical?.other || 0));
  
  const additionalTotal = (costs.additional?.total || 0) || 
    ((costs.marketing || 0) + 
     (costs.insurance || 0) + 
     (costs.visas || 0) + 
     (costs.other || 0));
  
  // Sum all costs except band fee
  const totalWithoutBandFee = 
    travelTotal + 
    accommodationTotal + 
    perDiemTotal + 
    equipmentTotal + 
    technicalTotal + 
    additionalTotal;
  
  // Add band fee
  const totalBeforeContingency = bandFee + totalWithoutBandFee;
  
  // Add contingency
  const contingencyRate = Number(performance.contingencyRate || 10);
  const contingencyAmount = (totalBeforeContingency * contingencyRate) / 100;
  
  const totalCost = totalBeforeContingency + contingencyAmount;
  
 
  
  return totalCost;
};

export const calculateTotalIncome = (performance: Performance): number => {
  const { revenue } = performance;
  return (revenue.ticketPrice || 0) * (revenue.expectedOccupancy || 0) +
         (revenue.sponsorship || 0) +
         (revenue.merchandiseSales || 0) +
         (revenue.otherRevenue || 0);
};

export const calculateProfitLoss = (performance: Performance): number => {
  return calculateTotalIncome(performance) - calculateTotalCost(performance);
};

export default Performance;